import { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/ui/SaveButton';
import { ExternalLink, Globe, EyeOff } from 'lucide-react';
import { EditorSidebarSection, EditorSidebarCard } from './EditorSidebar';

/**
 * EditorActionCard — Canonical Farmly action card for all editor sidebars.
 *
 * Structure (top → bottom):
 *   1. Status row: dot indicator + label (left), Publish/Unpublish action (right)
 *   2. Save + View row
 *   3. Optional secondary action row (e.g. share)
 *   4. Optional footer slot (e.g. delete, hints)
 *
 * Follows Farmly mutation policy:
 *   - Save = draft/content changes
 *   - Publish/Unpublish = immediate confirmed actions (auto-persist pending edits)
 *   - Status is informational, not editable
 */

export interface EditorActionCardProps {
  /** Whether the entity is currently published/public */
  isPublished: boolean;
  /** Label when published. Default: "Publicerad" */
  publishedLabel?: string;
  /** Label when draft. Default: "Utkast" */
  draftLabel?: string;

  /** Called when the user clicks Publish. Should auto-persist pending edits. */
  onPublish: () => void;
  /** Called when the user clicks Unpublish. Should auto-persist pending edits. */
  onUnpublish: () => void;
  /** Whether publish/unpublish is disabled (e.g. during mutation) */
  publishDisabled?: boolean;
  /** Custom publish button content — replaces default Publicera/Avpublicera.
   *  Use for confirmation dialogs (AlertDialog wrapping). */
  publishSlot?: ReactNode;

  /** Called when the user clicks Save */
  onSave: () => void;
  /** Whether save is in progress */
  isSaving: boolean;
  /** Whether save button is disabled */
  saveDisabled?: boolean;

  /** URL for the "Visa" preview link. If omitted, Visa button is hidden. */
  viewHref?: string;
  /** If provided, called on Visa click instead of navigating to viewHref */
  onView?: () => void;

  /** Optional full-width secondary action below save row (e.g. Share) */
  secondaryAction?: ReactNode;
  /** Optional footer below everything (e.g. delete button, hints) */
  footer?: ReactNode;
}

export function EditorActionCard({
  isPublished,
  publishedLabel = 'Publicerad',
  draftLabel = 'Utkast',
  onPublish,
  onUnpublish,
  publishDisabled = false,
  publishSlot,
  onSave,
  isSaving,
  saveDisabled = false,
  viewHref,
  onView,
  secondaryAction,
  footer,
}: EditorActionCardProps) {
  return (
    <EditorSidebarSection>
      <EditorSidebarCard>
        <div className="space-y-3">
          {/* Row 1: Status indicator + Publish/Unpublish */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-2 w-2 rounded-full',
                  isPublished ? 'bg-green-500' : 'bg-amber-500',
                )}
              />
              <span className="text-sm font-medium text-foreground">
                {isPublished ? publishedLabel : draftLabel}
              </span>
            </div>
            {publishSlot ?? (
              isPublished ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs"
                  onClick={onUnpublish}
                  disabled={publishDisabled}
                >
                  <EyeOff className="h-3.5 w-3.5 mr-1" />
                  Avpublicera
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="text-xs"
                  onClick={onPublish}
                  disabled={publishDisabled}
                >
                  <Globe className="h-3.5 w-3.5 mr-1" />
                  Publicera
                </Button>
              )
            )}
          </div>

          {/* Row 2: Save + View */}
          <div className="flex gap-2">
            <SaveButton
              isSaving={isSaving}
              disabled={saveDisabled}
              onClick={onSave}
              size="sm"
              className="flex-1"
            />
            {(viewHref || onView) && (
              onView ? (
                <Button variant="outline" size="sm" onClick={onView}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Visa
                </Button>
              ) : (
                <Button variant="outline" size="sm" asChild>
                  <a href={viewHref} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                    Visa
                  </a>
                </Button>
              )
            )}
          </div>

          {/* Optional secondary action */}
          {secondaryAction}

          {/* Optional footer */}
          {footer}
        </div>
      </EditorSidebarCard>
    </EditorSidebarSection>
  );
}
